import { erpCookies, migrateLegacyAuthCookies, clearLegacyAuthCookies } from "./erpCookies.js"

const LIVE_COOKIE_KEYS = ["tenant", "usuario", "idUsuario", "nombreUsuario", "rolUsuario"]
const STORAGE_TOKEN = "token"
const STORAGE_USER = "user"
const SESSION_PREFIX = "erpSession_"

/**
 * Primer segmento de la URL (`/buddypetserp/verVentasDetalle` → `buddypetserp`).
 * Las cookies de sesión son por origen (localhost), no por puerto ni tenant:
 * sin namespace + sync, abrir Autos u otro tenant reutiliza el JWT/cookie del anterior.
 */
export function getTenantFromPathname(pathname = typeof window !== "undefined" ? window.location.pathname : "") {
  const first = String(pathname || "").split("/").filter(Boolean)[0]
  if (!first || first === "login") return null
  return first
}

function sessionKey(tenant) {
  return `${SESSION_PREFIX}${tenant}`
}

function setLiveCookie(name, value, persistent = false) {
  if (value == null || value === "") {
    erpCookies.remove(name)
    return
  }
  erpCookies.set(name, String(value), { expires: persistent ? 365 : 1 })
}

export function readLiveSession() {
  const tenant = erpCookies.get("tenant")
  if (!tenant) return null
  return {
    tenant,
    usuario: erpCookies.get("usuario") || "",
    idUsuario: erpCookies.get("idUsuario") || "",
    nombreUsuario: erpCookies.get("nombreUsuario") || "",
    rolUsuario: erpCookies.get("rolUsuario") || "",
    token: localStorage.getItem(STORAGE_TOKEN),
    user: localStorage.getItem(STORAGE_USER),
  }
}

export function persistLiveSession(tenant = erpCookies.get("tenant")) {
  if (!tenant) return
  const token = localStorage.getItem(STORAGE_TOKEN)
  const user = localStorage.getItem(STORAGE_USER)
  if (!token && !user) return
  try {
    localStorage.setItem(
      sessionKey(tenant),
      JSON.stringify({
        tenant,
        usuario: erpCookies.get("usuario") || "",
        idUsuario: erpCookies.get("idUsuario") || "",
        nombreUsuario: erpCookies.get("nombreUsuario") || "",
        rolUsuario: erpCookies.get("rolUsuario") || "",
        token,
        user,
      }),
    )
  } catch (error) {
    console.warn("No se pudo guardar la sesión del tenant:", error)
  }
}

export function loadPersistedSession(tenant) {
  if (!tenant) return null
  try {
    const raw = localStorage.getItem(sessionKey(tenant))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.token && !parsed?.user) return null
    return parsed
  } catch {
    return null
  }
}

export function applySession(session) {
  if (!session?.tenant) {
    clearLiveSession()
    return
  }
  setLiveCookie("tenant", session.tenant, false)
  setLiveCookie("usuario", session.usuario, false)
  setLiveCookie("idUsuario", session.idUsuario, false)
  setLiveCookie("nombreUsuario", session.nombreUsuario, false)
  setLiveCookie("rolUsuario", session.rolUsuario, false)
  setLiveCookie("tenantURL", session.tenant, true)
  if (session.token) localStorage.setItem(STORAGE_TOKEN, session.token)
  else localStorage.removeItem(STORAGE_TOKEN)
  if (session.user) {
    localStorage.setItem(
      STORAGE_USER,
      typeof session.user === "string" ? session.user : JSON.stringify(session.user),
    )
  } else {
    localStorage.removeItem(STORAGE_USER)
  }
}

export function clearLiveSession() {
  LIVE_COOKIE_KEYS.forEach((name) => erpCookies.remove(name))
  localStorage.removeItem(STORAGE_TOKEN)
  localStorage.removeItem(STORAGE_USER)
}

export function clearPersistedSession(tenant) {
  if (!tenant) return
  localStorage.removeItem(sessionKey(tenant))
}

/**
 * Deja las cookies/token vivos alineados con el tenant de la URL.
 * Si había otra sesión activa, la guarda y recarga para no mezclar estado React.
 */
export function syncSessionToTenant(urlTenant) {
  if (!urlTenant) {
    return { needReload: false, session: readLiveSession() }
  }

  const liveTenant = erpCookies.get("tenant")

  if (!liveTenant) {
    const previousUrlTenant = erpCookies.get("tenantURL")
    if (previousUrlTenant && previousUrlTenant !== urlTenant) {
      persistLiveSession(previousUrlTenant)
    }
    erpCookies.set("tenantURL", urlTenant, { expires: 365 })
    const stored = loadPersistedSession(urlTenant)
    if (stored) {
      applySession({ ...stored, tenant: urlTenant })
      return { needReload: false, session: readLiveSession() }
    }
    localStorage.removeItem(STORAGE_TOKEN)
    localStorage.removeItem(STORAGE_USER)
    return { needReload: false, session: null }
  }

  if (liveTenant === urlTenant) {
    persistLiveSession(urlTenant)
    erpCookies.set("tenantURL", urlTenant, { expires: 365 })
    return { needReload: false, session: readLiveSession() }
  }

  persistLiveSession(liveTenant)
  const stored = loadPersistedSession(urlTenant)
  if (stored) {
    applySession({ ...stored, tenant: urlTenant })
  } else {
    clearLiveSession()
    erpCookies.set("tenantURL", urlTenant, { expires: 365 })
  }
  return { needReload: true, session: stored ? readLiveSession() : null }
}

export function hydrateTenantSession() {
  if (typeof window === "undefined") return { needReload: false, session: null }
  migrateLegacyAuthCookies()
  clearLegacyAuthCookies()
  const result = syncSessionToTenant(getTenantFromPathname(window.location.pathname))
  if (result.needReload) {
    window.location.replace(window.location.href)
  }
  return result
}
